import { useContext, useMemo } from 'react';
import { defer, type Observable, switchMap } from 'rxjs';

import useApiV2 from './use-api-v2';
import { AuthContext } from '../contexts/auth-context-provider';
import { type ApiHookType } from '../types';

export const useSecuredApiV2 = (): ApiHookType => {
  const auth = useContext(AuthContext);
  const api = useApiV2();

  return useMemo(() => {
    const withAuthHeaders = <T,>(call: (headers: HeadersInit) => Observable<T>): Observable<T> =>
      defer(() => auth.getValidAccessToken()).pipe(
        switchMap((token) =>
          call({
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          })
        )
      );

    return {
      get: (url, params, _, asBlob) =>
        withAuthHeaders((headers) => api.get(url, params, headers, asBlob)),

      post: (url, data, params, _, asBlob) =>
        withAuthHeaders((headers) => api.post(url, data, params, headers, asBlob)),

      put: (url, data, params, _) =>
        withAuthHeaders((headers) => api.put(url, data, params, headers)),

      patch: (url, data, params, _) =>
        withAuthHeaders((headers) => api.patch(url, data, params, headers)),

      delete: (url, data, params, _) =>
        withAuthHeaders((headers) => api.delete(url, data, params, headers)),
    };
  }, [auth, api]);
};
export default useSecuredApiV2;
